import { LocationOn, Search, Edit, ArrowBack } from '@mui/icons-material'
import { Autocomplete, Box, CircularProgress, Paper, TextField, Button, Grid } from '@mui/material'
import * as React from 'react'
import { useDebounce } from '../hooks/useDebounce'

export type AddressOption = {
  label: string
  housenumber: string | undefined
  street: string | undefined
  postcode: string | undefined
  city: string | undefined
  latitude: number | undefined
  longitude: number | undefined
  country: string | undefined
}

type AddressApiResponse = {
  features: {
    properties: {
      label: string
      housenumber: string
      street: string
      postcode: string
      city: string
    }
    geometry: {
      coordinates: number[]
    }
  }[]
}

type AddressProps = {
  label?: string
  address?: AddressOption | null
  onAddressChange?: (value: AddressOption | null) => void
  disabled?: boolean
  className?: string
  defaultSearch?: string
  error?: {
    street?: string
    postal_code?: string
    city?: string
  }
  required?: boolean
}

export function AddressInput({
  label,
  address,
  onAddressChange,
  className,
  disabled = false,
  defaultSearch,
  error,
  required = true,
}: AddressProps) {
  const [value, setValue] = React.useState<AddressOption | null>(null)
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState<string>('')
  const [loading, setLoading] = React.useState(false)
  const [options, setOptions] = React.useState<AddressOption[]>([])
  const [showManualInput, setShowManualInput] = React.useState(false)
  const [manualAddress, setManualAddress] = React.useState({
    street: address?.street || '',
    postal_code: address?.postcode || '',
    city: address?.city || '',
    country: address?.country || 'France',
  })

  const debouncedSearch = useDebounce(search, 600)

  React.useEffect(() => {
    if (debouncedSearch) {
      setLoading(true)
      const searchParams = new URLSearchParams({
        q: debouncedSearch,
        type: 'housenumber',
      })

      fetch(`https://api-adresse.data.gouv.fr/search/?${searchParams.toString()}`)
        .then((res) => res.json())
        .then((data: AddressApiResponse) => {
          const cities: AddressOption[] = data.features.map((f) => ({
            label: f.properties.label,
            housenumber: f.properties.housenumber,
            street: f.properties.street,
            postcode: f.properties.postcode,
            city: f.properties.city,
            country: 'France',
            longitude: f.geometry?.coordinates?.[0],
            latitude: f.geometry?.coordinates?.[1],
          }))

          if (cities.some((c: AddressOption) => !c.longitude || !c.latitude)) {
            console.warn('Missing longitude or latitude in address/search:', cities)
          }

          setOptions(cities)
          setLoading(false)
        })
        .catch((error: Error) => {
          console.error('Error fetching addresses:', error)
          setLoading(false)
        })
    }
  }, [debouncedSearch])

  React.useEffect(() => {
    if (!address && defaultSearch) {
      setSearch(defaultSearch)
    }
  }, [defaultSearch])

  React.useEffect(() => {
    if (address && !value) {
      onAddressChange?.(address)
      setValue(address)
    }
  }, [address, value])

  const addressLabel = [
    (address?.street || '') + (address?.street && address?.postcode ? ',' : ''),
    address?.postcode,
    address?.city || '',
  ]
    .filter((v) => Boolean(v))
    .join(' ')

  const handleManualAddressChange = (field: keyof typeof manualAddress, value: string) => {
    const newManualAddress = { ...manualAddress, [field]: value }
    setManualAddress(newManualAddress)

    if (newManualAddress.street || newManualAddress.postal_code || newManualAddress.city) {
      const newAddress: AddressOption = {
        label:
          `${newManualAddress.street}, ${newManualAddress.postal_code} ${newManualAddress.city}, ${newManualAddress.country}`.trim(),
        street: newManualAddress.street,
        postcode: newManualAddress.postal_code,
        city: newManualAddress.city,
        housenumber: undefined,
        country: newManualAddress.country,
        latitude: undefined,
        longitude: undefined,
      }
      setValue(newAddress)
      onAddressChange?.(newAddress)
    } else {
      setValue(null)
      onAddressChange?.(null)
    }
  }

  return (
    <Box className={className}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { lg: 'repeat(12, 1fr)' }, gap: 2 }}>
        <Box sx={{ gridColumn: { lg: 'span 12' } }}>
          {!showManualInput ? (
            <Autocomplete
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              disabled={disabled}
              loading={loading}
              options={options}
              getOptionLabel={(option) => option.label}
              inputValue={search || ''}
              onInputChange={(_, value) => setSearch(value)}
              onChange={(_, _value) => {
                if (_value && onAddressChange) {
                  setValue(_value)
                  onAddressChange(_value)
                } else {
                  setValue(null)
                  onAddressChange?.(null)
                }
                setOpen(false)
              }}
              value={value || null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label || "Adresse de mon logement pendant l'alternance"}
                  placeholder="Rechercher une adresse"
                  error={Boolean(error?.street || error?.postal_code || error?.city)}
                  helperText={error?.street || error?.postal_code || error?.city}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: addressLabel ? (
                      <LocationOn sx={{ ml: 1, color: 'text.secondary' }} />
                    ) : (
                      <Search sx={{ ml: 1, color: 'text.secondary' }} />
                    ),
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  required={required}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                    {option.label}
                  </Box>
                </li>
              )}
              noOptionsText={
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  {search?.length === 0 ? (
                    'Commencer à écrire pour lancer la recherche'
                  ) : (
                    <>
                      <Box sx={{ mb: 2 }}>Aucun résultat trouvé</Box>
                      <Button startIcon={<Edit />} variant="outlined" onClick={() => setShowManualInput(true)}>
                        Saisir l'adresse manuellement
                      </Button>
                    </>
                  )}
                </Box>
              }
              PaperComponent={({ children }) => <Paper sx={{ width: '100%' }}>{children}</Paper>}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button startIcon={<ArrowBack />} onClick={() => setShowManualInput(false)}>
                  Retour à l'outil de recherche
                </Button>
              </Box>
              <TextField
                label="Adresse"
                value={manualAddress.street}
                onChange={(e) => handleManualAddressChange('street', e.target.value)}
                error={Boolean(error?.street)}
                helperText={error?.street}
                fullWidth
                required={required}
              />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Code postal"
                    value={manualAddress.postal_code}
                    onChange={(e) => handleManualAddressChange('postal_code', e.target.value)}
                    error={Boolean(error?.postal_code)}
                    helperText={error?.postal_code}
                    fullWidth
                    required={required}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Ville"
                    value={manualAddress.city}
                    onChange={(e) => handleManualAddressChange('city', e.target.value)}
                    error={Boolean(error?.city)}
                    helperText={error?.city}
                    fullWidth
                    required={required}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Pays"
                    value={manualAddress.country}
                    onChange={(e) => handleManualAddressChange('country', e.target.value)}
                    fullWidth
                    required={required}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
